import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { TabTitleService } from '../../layout/tab-title.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { IdentityUserSelectorComponent } from '../../shared/design-system/identity-user-selector.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../shared/design-system/structured-json-display.component';
import { PlatformAdminService, UserPartyBindingRecord } from './platform-admin.service';
import {
  bindingStatusLabelKey,
  bindingStatusSeverity,
  relationTypeLabelKey
} from './platform-user-party-binding.utils';

@Component({
  standalone: true,
  selector: 'ain-platform-user-party-binding-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    FormsModule,
    ButtonModule,
    TagModule,
    RouterLink,
    AinDatePipe,
    IdentityUserSelectorComponent,
    PartySelectorComponent,
    CustomerSelectorComponent,
    StructuredJsonDisplayComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('platform.eyebrow')"
      [title]="t('platform.bindings.detail.title')"
      [description]="t('platform.bindings.detail.description')">
      <div page-actions>
        <a class="back-link" [routerLink]="['/platform/user-party-bindings']" [queryParams]="listQueryParams()">{{ t('catalog.action.backToList') }}</a>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!binding()) {
        <ain-state type="error" [title]="t('platform.bindings.detail.notFound')" />
      } @else {
        <section class="header-card">
          <div class="header-main">
            <div class="header-tags">
              <p-tag [severity]="statusSeverity(binding()!.status)" [value]="statusLabel(binding()!.status)" />
              <p-tag severity="secondary" [value]="relationTypeLabel(binding()!.relationType)" />
              @if (binding()!.isPrimary) {
                <p-tag severity="info" [value]="t('platform.bindings.fields.isPrimary')" />
              }
            </div>
            <p class="binding-id">{{ binding()!.id }}</p>
          </div>
          <div class="header-actions">
            <button pButton type="button" severity="success" [label]="t('platform.bindings.actions.verify')" (click)="verify()"></button>
            <button pButton type="button" severity="warn" [label]="t('platform.bindings.actions.suspend')" (click)="suspend()"></button>
            <button pButton type="button" severity="danger" [label]="t('platform.bindings.actions.revoke')" (click)="revoke()"></button>
          </div>
        </section>

        <section class="card">
          <h3>{{ t('platform.bindings.detail.overview') }}</h3>
          <div class="detail-grid">
            <label>
              <span>{{ t('platform.bindings.fields.user') }}</span>
              <ain-identity-user-selector [ngModel]="binding()!.userId" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('platform.bindings.fields.party') }}</span>
              <ain-party-selector [ngModel]="binding()!.partyId" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('platform.bindings.fields.customerAccount') }}</span>
              @if (binding()!.customerAccountId) {
                <ain-customer-selector [ngModel]="binding()!.customerAccountId!" [disabled]="true" />
              } @else {
                <span class="muted">—</span>
              }
            </label>
            <div>
              <span class="field-label">{{ t('platform.bindings.fields.createdAt') }}</span>
              <span>{{ binding()!.createdAtUtc | ainDate }}</span>
            </div>
            @if (binding()!.verifiedAtUtc) {
              <div>
                <span class="field-label">{{ t('platform.bindings.fields.verifiedAt') }}</span>
                <span>{{ binding()!.verifiedAtUtc | ainDate }}</span>
              </div>
            }
            @if (binding()!.revokedAtUtc) {
              <div>
                <span class="field-label">{{ t('platform.bindings.fields.revokedAt') }}</span>
                <span>{{ binding()!.revokedAtUtc | ainDate }}</span>
              </div>
            }
            @if (binding()!.correlationId) {
              <div>
                <span class="field-label">{{ t('platform.bindings.fields.correlationId') }}</span>
                <span>{{ binding()!.correlationId }}</span>
              </div>
            }
          </div>
        </section>

        @if (binding()!.evidenceJson?.trim()) {
          <section class="card">
            <h3>{{ t('platform.bindings.detail.evidence') }}</h3>
            <ain-structured-json-display [payloadJson]="binding()!.evidenceJson!" />
          </section>
        }
      }
    </ain-page>
  `,
  styles: [`
    .back-link { text-decoration: none; color: var(--ain-color-primary, #1d4ed8); font-weight: 600; }
    .header-card {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 12px;
      background: #f8fafc;
    }
    .header-tags { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
    .binding-id { margin: 0; font-size: 13px; color: #64748b; }
    .header-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: flex-start; }
    .card {
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 12px;
    }
    .card h3 { margin: 0 0 12px; font-size: 15px; }
    .detail-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px 16px;
    }
    .detail-grid label { display: grid; gap: 6px; }
    .detail-grid span.field-label {
      display: block;
      font-size: 12px;
      font-weight: 600;
      color: #475569;
      margin-bottom: 4px;
    }
    .muted { color: #64748b; }
    @media (max-width: 900px) { .detail-grid { grid-template-columns: 1fr; } }
  `]
})
export class PlatformUserPartyBindingDetailComponent implements OnInit {
  private readonly api = inject(PlatformAdminService);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);
  private readonly tabTitleService = inject(TabTitleService);
  private readonly messages = inject(MessageService);

  readonly loading = signal(true);
  readonly binding = signal<UserPartyBindingRecord | null>(null);

  ngOnInit(): void {
    this.tabTitleService.setCurrent(this.t('platform.bindings.detail.title'));
    const bindingId = this.route.snapshot.paramMap.get('bindingId');
    if (!bindingId) {
      this.loading.set(false);
      return;
    }
    this.load(bindingId);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  listQueryParams(): Record<string, string> {
    const params = this.route.snapshot.queryParamMap;
    const result: Record<string, string> = {};
    for (const key of ['userId', 'partyId', 'customerAccountId', 'relationType', 'status']) {
      const value = params.get(key);
      if (value) {
        result[key] = value;
      }
    }
    return result;
  }

  relationTypeLabel(value: number | string): string {
    const key = relationTypeLabelKey(value);
    return key ? this.t(key) : `${value}`;
  }

  statusLabel(value: number | string): string {
    const key = bindingStatusLabelKey(value);
    return key ? this.t(key) : `${value}`;
  }

  statusSeverity(value: number | string): 'success' | 'warn' | 'danger' | 'secondary' {
    return bindingStatusSeverity(value);
  }

  load(bindingId: string): void {
    this.loading.set(true);
    this.api.getUserPartyBinding(bindingId).subscribe({
      next: row => {
        this.binding.set(row);
        this.loading.set(false);
      },
      error: () => {
        this.messages.add({ severity: 'error', summary: this.t('platform.bindings.errorLoad') });
        this.loading.set(false);
      }
    });
  }

  verify(): void {
    const row = this.binding();
    if (!row) return;
    this.api.verifyUserPartyBinding(row.id).subscribe({
      next: updated => {
        this.binding.set(updated);
        this.messages.add({ severity: 'success', summary: this.t('platform.bindings.messages.verified') });
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('platform.bindings.errorTransition') })
    });
  }

  suspend(): void {
    const row = this.binding();
    if (!row) return;
    this.api.suspendUserPartyBinding(row.id).subscribe({
      next: updated => {
        this.binding.set(updated);
        this.messages.add({ severity: 'success', summary: this.t('platform.bindings.messages.suspended') });
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('platform.bindings.errorTransition') })
    });
  }

  revoke(): void {
    const row = this.binding();
    if (!row) return;
    this.api.revokeUserPartyBinding(row.id).subscribe({
      next: updated => {
        this.binding.set(updated);
        this.messages.add({ severity: 'success', summary: this.t('platform.bindings.messages.revoked') });
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('platform.bindings.errorTransition') })
    });
  }
}
